import { useState, useEffect, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

const STORAGE_KEY = 'bible-study-notes';

/**
 * Hook pour gérer les notes avec Supabase (PostgreSQL)
 * Fallback automatique sur localStorage si Supabase n'est pas configuré
 */
export const useNotesSupabase = (context = null) => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [useLocalStorage, setUseLocalStorage] = useState(!isSupabaseConfigured());

    // Charger depuis localStorage (fallback)
    const loadFromLocalStorage = useCallback(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const allNotes = JSON.parse(stored);
                const filtered = context
                    ? allNotes.filter(n => n.context === context)
                    : allNotes;
                return filtered;
            }
            return [];
        } catch (err) {
            console.error('❌ Erreur localStorage:', err);
            return [];
        }
    }, [context]);

    // Sauvegarder dans localStorage
    const saveToLocalStorage = useCallback((notesToSave) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(notesToSave));
        } catch (err) {
            console.error('❌ Erreur sauvegarde localStorage:', err);
        }
    }, []);

    // Charger toutes les notes depuis Supabase
    const loadNotes = useCallback(async () => {
        setLoading(true);
        setError(null);

        if (useLocalStorage) {
            const localNotes = loadFromLocalStorage();
            setNotes(localNotes);
            setLoading(false);
            console.log(`📦 ${localNotes.length} notes chargées depuis localStorage`);
            return localNotes;
        }

        try {
            let query = supabase
                .from('notes')
                .select('*')
                .order('created_at', { ascending: false });

            if (context) {
                query = query.eq('context', context);
            }

            const { data, error: supabaseError } = await query;

            if (supabaseError) throw supabaseError;

            setNotes(data || []);
            setLoading(false);
            console.log(`✅ ${data?.length || 0} notes chargées depuis Supabase`);
            return data || [];
        } catch (err) {
            console.error('❌ Erreur Supabase, basculement sur localStorage:', err);
            setUseLocalStorage(true);
            const localNotes = loadFromLocalStorage();
            setNotes(localNotes);
            setLoading(false);
            return localNotes;
        }
    }, [context, useLocalStorage, loadFromLocalStorage]);

    // Créer une nouvelle note
    const createNote = useCallback(async (noteData) => {
        setLoading(true);
        setError(null);

        const newNote = {
            ...noteData,
            context: noteData.context || context,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };

        if (useLocalStorage) {
            const id = Date.now().toString() + Math.random().toString(36);
            const noteWithId = { ...newNote, id };
            const stored = localStorage.getItem(STORAGE_KEY);
            const allNotes = stored ? JSON.parse(stored) : [];
            allNotes.unshift(noteWithId);
            saveToLocalStorage(allNotes);
            setNotes(prevNotes => [noteWithId, ...prevNotes]);
            setLoading(false);
            console.log('📦 Note créée dans localStorage');
            return noteWithId;
        }

        try {
            const { data, error: supabaseError } = await supabase
                .from('notes')
                .insert([newNote])
                .select()
                .single();

            if (supabaseError) throw supabaseError;

            setNotes(prevNotes => [data, ...prevNotes]);
            setLoading(false);
            console.log('✅ Note créée dans Supabase');
            return data;
        } catch (err) {
            console.error('❌ Erreur création note:', err);
            setError(err.message);
            setLoading(false);
            throw err;
        }
    }, [context, useLocalStorage, saveToLocalStorage]);

    // Mettre à jour une note
    const updateNote = useCallback(async (noteId, updates) => {
        setLoading(true);
        setError(null);

        const updatedData = {
            ...updates,
            updated_at: new Date().toISOString(),
        };

        if (useLocalStorage) {
            const stored = localStorage.getItem(STORAGE_KEY);
            const allNotes = stored ? JSON.parse(stored) : [];
            const index = allNotes.findIndex(n => n.id === noteId);
            if (index !== -1) {
                allNotes[index] = { ...allNotes[index], ...updatedData };
                saveToLocalStorage(allNotes);
                setNotes(prevNotes =>
                    prevNotes.map(note => note.id === noteId ? { ...note, ...updatedData } : note)
                );
            }
            setLoading(false);
            console.log('📦 Note mise à jour dans localStorage');
            return allNotes[index];
        }

        try {
            const { data, error: supabaseError } = await supabase
                .from('notes')
                .update(updatedData)
                .eq('id', noteId)
                .select()
                .single();

            if (supabaseError) throw supabaseError;

            setNotes(prevNotes =>
                prevNotes.map(note => note.id === noteId ? data : note)
            );
            setLoading(false);
            console.log('✅ Note mise à jour dans Supabase');
            return data;
        } catch (err) {
            console.error('❌ Erreur mise à jour note:', err);
            setError(err.message);
            setLoading(false);
            throw err;
        }
    }, [useLocalStorage, saveToLocalStorage]);

    // Supprimer une note
    const deleteNote = useCallback(async (noteId) => {
        setLoading(true);
        setError(null);

        if (useLocalStorage) {
            const stored = localStorage.getItem(STORAGE_KEY);
            const allNotes = stored ? JSON.parse(stored) : [];
            const filtered = allNotes.filter(n => n.id !== noteId);
            saveToLocalStorage(filtered);
            setNotes(prevNotes => prevNotes.filter(note => note.id !== noteId));
            setLoading(false);
            console.log('📦 Note supprimée de localStorage');
            return true;
        }

        try {
            const { error: supabaseError } = await supabase
                .from('notes')
                .delete()
                .eq('id', noteId);

            if (supabaseError) throw supabaseError;

            setNotes(prevNotes => prevNotes.filter(note => note.id !== noteId));
            setLoading(false);
            console.log('✅ Note supprimée de Supabase');
            return true;
        } catch (err) {
            console.error('❌ Erreur suppression note:', err);
            setError(err.message);
            setLoading(false);
            throw err;
        }
    }, [useLocalStorage, saveToLocalStorage]);

    // Rechercher dans les notes
    const searchNotes = useCallback(async (searchTerm) => {
        if (useLocalStorage) {
            const allNotes = loadFromLocalStorage();
            const filtered = allNotes.filter(note =>
                note.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                note.content?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setNotes(filtered);
            return filtered;
        }

        try {
            const { data, error: supabaseError } = await supabase
                .from('notes')
                .select('*')
                .or(`title.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%`)
                .order('created_at', { ascending: false });

            if (supabaseError) throw supabaseError;

            setNotes(data || []);
            return data || [];
        } catch (err) {
            console.error('❌ Erreur recherche:', err);
            return [];
        }
    }, [useLocalStorage, loadFromLocalStorage]);

    // Exporter toutes les notes
    const exportNotes = useCallback(async () => {
        const exportData = {
            export_date: new Date().toISOString(),
            total_notes: notes.length,
            notes: notes,
            source: useLocalStorage ? 'localStorage' : 'Supabase'
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `notes-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        console.log('📤 Notes exportées:', exportData.total_notes);
        return exportData;
    }, [notes, useLocalStorage]);

    // Charger les notes au montage
    useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    return {
        notes,
        loading,
        error,
        loadNotes,
        createNote,
        updateNote,
        deleteNote,
        searchNotes,
        exportNotes,
        isUsingLocalStorage: useLocalStorage,
        isUsingSupabase: !useLocalStorage,
    };
};

export default useNotesSupabase;
